<template>
  <div
    ref="tableWrapperRef"
    class="sm-table table-wrapper !h-full"
    style="
      display: flex;
      flex-direction: column;
      height: 100%;
      position: relative;
    ">
    <!-- Table Header -->
    <div
      v-if="!hideHeader"
      class="flex shrink-0 grow-0 basis-auto border-b border-gray-200 pb-3 dark:border-gray-700"
      :style="{ paddingRight: headerPadding + 'px' }">
      <slot name="header">
        <!-- Select all -->
        <div
          v-if="props.selectable"
          style="width: 40px; display: flex; justify-content: center"
          @click="
            everythingSelected = props.selectDisabled
              ? everythingSelected
              : !everythingSelected
          ">
          <SmSpinner v-if="loadCheckbox" />
          <input
            v-else
            v-model="everythingSelected"
            type="checkbox"
            :disabled="props.selectDisabled"
            @click.stop />
        </div>
        <!-- Expand Handle -->
        <div
          v-if="props.expandable && !props.draggable && !props.selectable"
          style="width: 30px; display: flex; justify-content: center"></div>
        <!-- Drag Handle -->
        <div
          v-if="props.draggable"
          style="width: 26px; display: flex; justify-content: center"></div>
        <div
          v-for="column in props.columns"
          :key="column.key"
          class="sm-table-header-cell px-3"
          :style="{
            ...cellStyle(column),
          }"
          @click="handleHeaderClick(column.key)">
          <!-- Header Label -->
          <slot :name="`header-${column.key}`" :column="column">
            <TextMultiline :text="column.label" :show-tool-tip="false" />
          </slot>
          <!-- Sorting -->
          <div v-if="column.sortable !== false" :class="{ sort: true }">
            <v-icon
              name="io-caret-down-outline"
              scale="0.85"
              :class="{
                'sort-icon': true,
                asc: sorting.asc && sorting.by === column.key,
                active: sorting.by === column.key,
              }" />
          </div>
        </div>
      </slot>
    </div>

    <!-- Table Content -->
    <div
      class="sm-table-content scroller shrink grow basis-auto"
      :style="{
        maxHeight: `calc(100% - ${hasFooter && !hideHeader ? 64 : hasFooter || !hideHeader ? 32 : 0}px)`,
      }">
      <!-- Loading (Skeleton)-->
      <template v-if="props.loading">
        <VList
          ref="scrollerRef"
          v-slot="{ item }"
          :data="loadingData"
          :style="containStrictStyle">
          <div :style="rowStyle">
            <SmTableRow
              :loading="true"
              :data="item"
              key-field="index"
              :columns="columns" />
          </div>
        </VList>
      </template>

      <template v-else-if="!props.draggable">
        <VList
          v-if="data.length > 0"
          ref="scrollerRef"
          v-slot="{ item, index }"
          :data="data"
          :style="containStrictStyle"
          :overscan="20"
          @scroll="handleScroll">
          <component
            :is="props.rowLink ? 'a' : 'div'"
            :key="item[props.keyField]"
            :class="[
              {
                'border-b-0': index === data.length - 1,
                'cursor-pointer': hasCellClickFn,
              },
            ]"
            :style="rowStyle"
            :href="props.rowLink ? props.rowLink(item) : undefined"
            @click="handleLinkClick">
            <SmTableRow
              :data="item"
              :key-field="props.keyField"
              :selectable="selectable"
              :expandable="props.expandable"
              :select-disabled="selectDisabled"
              :is-checked="checkSelection(item)"
              :columns="columns"
              :is-last-row="index === data.length - 1"
              :is-clickable="!!props.rowLink || hasCellClickFn"
              @select="handleCheckboxSelect"
              @deselect="handleCheckboxDeselect"
              @handle-cell-click="handleCellClick"
              @expand="handleRowExpand">
              <template
                v-for="column in columns"
                :key="column.key"
                #[column.key]>
                <slot :name="column.key" :row="item"></slot>
              </template>
              <!-- expand only integrated on non draggable rows for now -->
              <template #expand>
                <slot name="expand" :row="item"></slot>
              </template>
            </SmTableRow>
          </component>
        </VList>
      </template>

      <template v-else>
        <VueDraggable
          v-if="data.length > 0"
          ref="scrollerRef"
          v-model="data"
          :animation="150"
          handle=".handle">
          <div v-for="(item, index) in data" :key="item._id" :style="rowStyle">
            <SmTableRow
              :data="item"
              :selectable="selectable"
              :columns="columns"
              :key-field="props.keyField"
              :is-last-row="index === data.length - 1"
              :is-clickable="!!props.rowLink || hasCellClickFn"
              draggable
              @handle-cell-click="handleCellClick"
              @expand="handleRowExpand">
              <template
                v-for="column in columns"
                :key="column.key"
                #[column.key]>
                <slot :name="column.key" :row="item" :index="index"></slot>
              </template>
            </SmTableRow>
          </div>
        </VueDraggable>
      </template>

      <!-- No Data -->
      <div
        v-if="!props.loading && modelData.length === 0"
        class="no-data"
        style="
          flex: 1;
          display: flex;
          justify-content: center;
          align-items: center;
        ">
        <slot name="no-data">
          <span style="padding: 10px 20px">{{
            i18n.t('tableNoEntries')
          }}</span>
        </slot>
      </div>
    </div>

    <!-- Table Footer -->
    <div
      v-if="hasFooter"
      class="flex shrink-0 grow-0 basis-auto border-t border-gray-200 pt-3 dark:border-gray-700"
      :style="{ paddingRight: headerPadding + 'px' }">
      <slot name="footer">
        <div
          v-if="props.selectable"
          style="width: 40px; display: flex; justify-content: center"></div>
        <div
          v-if="props.expandable && !props.draggable && !props.selectable"
          style="width: 30px; display: flex; justify-content: center"></div>
        <div
          v-if="props.draggable"
          style="width: 26px; display: flex; justify-content: center"></div>
        <div
          v-for="(column, index) in props.columns"
          :key="column.key"
          class="flex"
          :style="{ padding: '0 12px', ...cellStyle(column) }">
          <!-- Loader -->
          <SmSkeleton
            v-if="props.loading && (column.footer?.value ?? column.footer)" />

          <!-- Footer Label -->
          <slot v-else :name="`footer-${column.key}`" :column="column">
            <p class="text-contrast-muted">
              {{ column.footer?.value ?? column.footer }}
            </p>
          </slot>
        </div>
      </slot>
    </div>
  </div>
</template>

<script setup generic="T extends TableData" lang="ts">
  export interface TableData {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any
    sorting?: number
  }

  import { useElementSize } from '@vueuse/core'
  import { useI18n } from 'vue-i18n'

  import { Column, Sorting } from './SmTable.types'
  import TextMultiline from './TextMultiline.vue'
  import SmTableRow from './SmTableRow.vue'
  import { VueDraggable } from 'vue-draggable-plus'
  import { TagsService, SortableOrderUpdate } from '@/client'
  import { getCurrentInstance } from 'vue'
  import { differenceBy } from 'lodash-es'
  import { VList } from 'virtua/vue'
  const i18n = useI18n()

  const scrollerRef = ref<InstanceType<typeof VList> | null>(null)
  const tableWrapperRef = ref<HTMLDivElement>()
  const slots = useSlots()

  const hasCellClickFn = computed(() => {
    // check if a onCellClick Event exists
    const instance = getCurrentInstance()
    return !!instance?.vnode?.props?.onCellClick
  })

  interface Props {
    rowLink?: (row: T) => string
    keyField: keyof T
    columns: Column<T>[]
    itemHeight?: string
    loading?: boolean
    selectable?: boolean
    selectDisabled?: boolean
    notScrollable?: boolean
    defaultSorting?: Sorting
    hideHeader?: boolean
    externalSorting?: boolean
    rowStyle?: Record<string, string>
    draggable?: boolean
    expandable?: boolean
    loadAllFn?: () => Promise<T[]>
    containStrict?: boolean
  }

  // Define Props
  const props = withDefaults(defineProps<Props>(), {
    rowLink: undefined,
    itemHeight: '50px',
    loading: false,
    selectable: false,
    selectDisabled: false,
    columns: () => [],
    notScrollable: false,
    defaultSorting: () => {
      return {
        by: 'email',
        asc: false,
      }
    },
    hideHeader: false,
    externalSorting: false,
    rowStyle: () => {
      return {}
    },
    draggable: false,
    expandable: false,
    loadAllFn: undefined,
    containStrict: false,
  })
  const containStrictStyle = computed(() => {
    return props.containStrict ? { contain: 'strict' } : { contain: 'none' }
  })

  const key: keyof T = props.keyField as keyof T

  const emit = defineEmits<{
    // Click events
    'cell-click': [iRow: number, columnKey: string, row: T]
    'row-click': [iRow: number, row: T]
    'column-click': [columnKey: string]
    'row-expand': [row: T, expanded: boolean]

    // Drag Events
    'row-drag': []

    // Selection Events
    'select-change': [selection: T[]]

    // Srcoll Events
    'scroll-end-reached': [currentLength: number]
  }>()

  //   Click Handlers
  function handleCellClick(data: { iRow: number; row: T; columnKey: string }) {
    emit('cell-click', data.iRow, data.columnKey, data.row)
    emit('row-click', data.iRow, data.row)
    emit('column-click', data.columnKey)
  }

  function handleRowExpand(row: T, expanded: boolean) {
    emit('row-expand', row, expanded)
  }

  const sorting = ref(props.defaultSorting)

  const modelData = defineModel<T[]>('data', { required: true })

  const isExternalSorting = computed(() => {
    return props.draggable ? true : props.externalSorting
  })

  const hasFooter: Ref<boolean> = computed(() => {
    return (
      Object.keys(slots).some((slotName) => slotName.startsWith('footer')) ||
      props.columns.some((column) => 'footer' in column)
    )
  })

  const data = computed({
    get() {
      if (modelData.value.length > 0 && !isExternalSorting.value) {
        return sortByColumn(sorting.value.by, sorting.value.asc)
      } else {
        return modelData.value
      }
    },
    set(newValue) {
      modelData.value = newValue
    },
  })

  if (props.draggable) {
    watch(data, (newData) => {
      const idArray: SortableOrderUpdate = {
        _ids: newData.map((item) => item._id),
      }

      TagsService.reorderTagsApiV1ManagementTagsReorderPost({
        requestBody: idArray,
      }).then(() => {
        newData.forEach((item, index: number) => {
          item.sorting = newData.length - index
        })
        emit('row-drag')
      })
    })
  }

  const createLoadingData = (length: number) => {
    return Array.from({ length }, (_, i) => ({ index: i }))
  }
  const loadingData = ref(createLoadingData(5))

  //   Cell Style
  const columnStyleCache: Record<string, Record<string, number | string>> = {}
  function cellStyle(column: Column<T>) {
    // Check cache
    if (columnStyleCache[column.key]) {
      return columnStyleCache[column.key]
    }

    const style: Record<string, number | string> = {}

    // Get width
    if (typeof column.width === 'number') {
      style['flex'] = `${column.width}`
    } else {
      style['width'] = column.width
    }

    // set alignment
    if (column.align) {
      style['justify-content'] = column.align
    }

    // Cache style
    if (!columnStyleCache[column.key]) {
      columnStyleCache[column.key] = {}
    }
    columnStyleCache[column.key] = style

    // Return style
    return style
  }

  // ###########################################################
  // ##################### Sorting #############################
  // ###########################################################
  function sortByColumn(key: keyof T, asc: boolean) {
    const columnDefinition = props.columns?.find((c) => c.key === key)
    let sortFn = columnDefinition?.sortFn

    // Default sort function
    if (!sortFn) {
      sortFn = (a: T, b: T) => {
        if (!a[key] && !b[key]) {
          return 0
        }
        if (!a[key]) {
          return -1
        }
        if (!b[key]) {
          return 1
        }
        if (a[key] < b[key]) {
          return -1
        }
        if (a[key] > b[key]) {
          return 1
        }
        return 0
      }
    }

    const sorted = [...modelData.value].sort(sortFn)

    return asc ? sorted : sorted.reverse()
  }

  function handleHeaderClick(key: string) {
    if (props.columns?.find((c) => c.key === key)?.sortable === false) {
      return // Column is not sortable
    }
    changeSorting(key)
  }

  function changeSorting(key: string) {
    if (sorting.value.by === key) {
      sorting.value.asc = !sorting.value.asc
    } else {
      sorting.value.by = key
      sorting.value.asc = true
    }
  }

  // ###########################################################
  // ##################### Selection ###########################
  // ###########################################################
  const selection: Ref<Array<T>> = ref([])
  const loadCheckbox = ref(false)

  const everythingSelected = computed({
    get: () => {
      return (
        selection.value.length > 0 &&
        modelData.value.every((i) =>
          selection.value.some((s) => s[props.keyField] === i[props.keyField])
        )
      )
    },
    set: (value) => {
      if (value) {
        selectAll()
      } else {
        deselectAll()
      }
    },
  })

  // Select
  function select(item: T) {
    if (
      !selection.value.some((s) => s[props.keyField] === item[props.keyField])
    ) {
      selection.value.push(item)
      emit('select-change', selection.value)
    }
  }

  function selectById(id: string) {
    const item = modelData.value.find((i) => i[key] === id)
    if (item) {
      select(item)
    }
  }

  function selectByIds(ids: string[]) {
    ids.forEach((id) => selectById(id))

    return selection.value
  }

  function selectAll() {
    if (props.loadAllFn) {
      const timer = setTimeout(() => (loadCheckbox.value = true), 100)

      props
        .loadAllFn()
        .then((allItems) => {
          const newItems = differenceBy(
            allItems,
            selection.value,
            props.keyField
          )
          selection.value.push(...newItems)
          emit('select-change', selection.value)
        })
        .finally(() => {
          clearTimeout(timer)
          loadCheckbox.value = false
        })
    } else {
      selection.value = modelData.value
    }
  }

  // Deselect
  function deselect(item: T) {
    selection.value = selection.value.filter(
      (i) => i[props.keyField] !== item[props.keyField]
    )
    emit('select-change', selection.value)
  }

  function deselectById(id: string) {
    const item = modelData.value.find((i) => i[key] === id)
    if (item) {
      deselect(item)
    }
  }

  function deselectAll() {
    if (props.loadAllFn) {
      const timer = setTimeout(() => (loadCheckbox.value = true), 100)

      props
        .loadAllFn()
        .then((allItems) => {
          selection.value = differenceBy(
            selection.value,
            allItems,
            props.keyField
          )
          emit('select-change', selection.value)
        })
        .finally(() => {
          clearTimeout(timer)
          loadCheckbox.value = false
        })
    } else {
      selection.value = differenceBy(
        selection.value,
        modelData.value,
        props.keyField
      )
    }
  }

  // Genreal functions
  function resetSelection() {
    selection.value = []
  }

  function toggle(item: T) {
    if (
      selection.value.some((s) => s[props.keyField] === item[props.keyField])
    ) {
      deselect(item)
    } else {
      select(item)
    }
  }

  // Checkbox trigger
  function handleCheckboxSelect(item: T) {
    select(item)
  }

  function handleCheckboxDeselect(item: T) {
    deselect(item)
  }

  function checkSelection(item: T) {
    return selection.value.some(
      (s) => s[props.keyField] === item[props.keyField]
    )
  }

  // ###########################################################

  const { width: scrollerWidth } = useElementSize(() => scrollerRef.value?.$el)
  const { width: tableWrapperWidth } = useElementSize(tableWrapperRef)

  const headerPadding = computed(() => {
    if (scrollerWidth.value === 0) {
      return 0
    } else {
      return tableWrapperWidth.value - scrollerWidth.value
    }
  })

  // ###########################################################
  // ##################### Srolling ############################
  // ###########################################################

  const LOAD_THRESHOLD_PX = 800
  let lastScrollEmitted = 0

  function handleScroll(offset: number) {
    if (!scrollerRef.value) return
    const target = scrollerRef.value.$el as HTMLElement

    const scrollableDistance = Math.max(
      0,
      target.scrollHeight - target.offsetHeight
    )

    if (
      offset >= scrollableDistance - LOAD_THRESHOLD_PX &&
      modelData.value.length !== lastScrollEmitted
    ) {
      // Your infinite loading logic here
      lastScrollEmitted = modelData.value.length
      emit('scroll-end-reached', modelData.value.length)
    }
  }

  function scrollTo(key: string) {
    const targetIndex = data.value.findIndex(
      (item) => item[props.keyField] === key
    )
    const scroller = scrollerRef
    if (scroller && targetIndex !== -1) {
      scroller.value?.scrollToIndex(targetIndex)
    }
  }

  // Reset last scroll emitted when data changes
  watch(
    () => modelData.value,
    () => {
      lastScrollEmitted = 0
    }
  )

  const handleLinkClick = (e: Event) => {
    if (props.rowLink) {
      e.preventDefault()
    }
  }

  onMounted(() => {
    //   Set initial sorting
    changeSorting(props.defaultSorting.by)
  })

  //   Define Exposes
  defineExpose({
    selection,
    changeSorting,
    resetSelection,
    select,
    selectById,
    selectByIds,
    deselect,
    deselectById,
    toggle,
    sorting,
    scrollTo,
    table: tableWrapperRef,
  })
</script>

<style lang="scss">
  $gap: 1.5rem;
  $select-width: 40px;

  .sm-table {
    .sm-table-content {
      display: flex;
      flex-direction: column;
      font-size: 0.95rem;
    }

    .sm-table-header {
      &-cell {
        display: flex;
        justify-content: left;
        align-items: center;
        font-weight: 600;
      }
    }

    .sort {
      display: flex;
      justify-content: center;
      align-items: center;
      cursor: pointer;
      margin-left: 5px;

      &-icon {
        color: var(--el-text-color-disabled);
        transition:
          rotate 0.2s ease-in-out,
          color 0.2s ease-in-out;

        &.asc {
          rotate: 180deg;
        }

        &.active {
          color: var(--el-color-primary);
        }

        &:hover {
          color: var(--el-color-primary);
        }
      }

      .scroller {
        overflow-y: auto;
        position: relative;
        height: 100%;
      }
    }

    input {
      accent-color: var(--el-color-primary);
      cursor: pointer;
    }
  }
</style>
